import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import type { CropperImage } from 'cropperjs';

import type { MediaCropArea } from '#src/app/shared/hooks/imageUpload';

import { getImageScale, moveImageTo, naturalOffsetToTranslate } from '../coverTransform';

export type NaturalSize = { width: number; height: number };

type Props = {
	imageRef: RefObject<CropperImage | null>;
	image?: string;
	crop?: MediaCropArea;
};

/**
 * Captures the loaded image's natural size and applies the initial crop once the image is ready.
 *
 * cropper-image only exposes the loaded image's natural size via the promise `$ready()` resolves
 * with, so it's captured once for reuse in the (much more frequent) crop/clamp handlers.
 */
export function useImageReady({ imageRef, image, crop }: Props): RefObject<NaturalSize | undefined> {
	const naturalSizeRef = useRef<NaturalSize | undefined>(undefined);

	useEffect(() => {
		/** Because of the fixed viewport, we set the initial crop by moving the image behind it. */
		const setInitialPan = () => {
			const imageElement = imageRef.current;
			const naturalSize = naturalSizeRef.current;
			const scale = getImageScale(imageElement);
			if (crop && imageElement && naturalSize && scale) {
				moveImageTo(
					imageElement,
					naturalOffsetToTranslate(crop.x, naturalSize.width, scale),
					naturalOffsetToTranslate(crop.y, naturalSize.height, scale),
					scale,
				);
			}
		};
		const readyImage = async () => {
			try {
				const loadedImage = await imageRef.current?.$ready();
				if (!loadedImage) return;
				naturalSizeRef.current = { width: loadedImage.naturalWidth, height: loadedImage.naturalHeight };
				setInitialPan();
			} catch {
				// the image failed to load — nothing to position
			}
		};
		void readyImage();
		// only run once, when the image first becomes ready
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [image]);

	return naturalSizeRef;
}
